import React, { createContext, useCallback, useContext, useMemo } from 'react'

import useAuth from 'hooks/use-auth'
import { useGetExternalIntegrationsStatusChecks } from 'queries/external-integrations/use-get-external-integrations-status-checks'
import { externalIntegrationsStatusChecksGet } from 'utils/endpoints-access'
import {
  completeTeamExternalIntegrations,
  convertResponseToTeamExternalIntegration,
  removeSavedTeamExternalIntegrations
} from 'utils/team-external-integrations/team-external-integrations-util'

import type { AxiosError } from 'axios'
import type { QueryObserverResult } from 'react-query'
import type { TeamExternalIntegration } from 'types/api/status-checks/status-check'

export type IntegrationResponse = {
  integrations: TeamExternalIntegration[]
}

export interface StatusCheckExternalIntegrationsContextValues {
  externalIntegrations?: IntegrationResponse
  isFetchingExternalIntegrations: boolean
  hasErrorExternalIntegrations: boolean
  externalIntegrationsError: AxiosError | null
  getAvailableExternalIntegrations: (
    removeSavedIntegrations: boolean
  ) => TeamExternalIntegration[]
  reFetchExternalIntegrations(): Promise<
    QueryObserverResult<IntegrationResponse, AxiosError>
  >
  evaluatedTeamId: string
}

export const StatusCheckExternalIntegrationsContext = createContext<
  StatusCheckExternalIntegrationsContextValues | undefined
>(undefined)

export const StatusCheckExternalIntegrationsProvider: React.FC<{
  teamId?: string
}> = ({ teamId, children }) => {
  const { currentTeamId, hasEndpointAccess } = useAuth()

  const evaluatedTeamId = teamId ?? currentTeamId

  const canReadStatusChecks = hasEndpointAccess(
    externalIntegrationsStatusChecksGet,
    {
      teamId
    }
  )

  const {
    error: externalIntegrationsError,
    data: externalIntegrations,
    isFetching: isFetchingExternalIntegrations,
    isError: hasErrorExternalIntegrations,
    refetch: reFetchExternalIntegrations
  } = useGetExternalIntegrationsStatusChecks(evaluatedTeamId, {
    enabled: teamId ? canReadStatusChecks : true // TODO EN-7894 for backwards compatibility, older pages are not handling when priv is not met. In those cases, allow network call to be made and BE will handle the priv check
  })

  const savedTeamExternalIntegrations =
    convertResponseToTeamExternalIntegration(externalIntegrations)

  const getAvailableExternalIntegrations = useCallback(
    (removeSavedIntegrations) => {
      return removeSavedIntegrations
        ? removeSavedTeamExternalIntegrations(
            completeTeamExternalIntegrations('STATUS_CHECK', [], true),
            savedTeamExternalIntegrations
          )
        : completeTeamExternalIntegrations(
            'STATUS_CHECK',
            savedTeamExternalIntegrations,
            true
          )
    },
    [savedTeamExternalIntegrations]
  )

  const values = useMemo(() => {
    return {
      evaluatedTeamId,
      externalIntegrations,
      isFetchingExternalIntegrations,
      hasErrorExternalIntegrations,
      externalIntegrationsError,
      getAvailableExternalIntegrations,
      reFetchExternalIntegrations
    }
  }, [
    evaluatedTeamId,
    externalIntegrations,
    externalIntegrationsError,
    getAvailableExternalIntegrations,
    hasErrorExternalIntegrations,
    isFetchingExternalIntegrations,
    reFetchExternalIntegrations
  ])

  return (
    <StatusCheckExternalIntegrationsContext.Provider value={values}>
      {children}
    </StatusCheckExternalIntegrationsContext.Provider>
  )
}

export function useStatusCheckExternalIntegrationsContext() {
  const context = useContext(StatusCheckExternalIntegrationsContext)

  if (!context) {
    throw new Error(
      'useStatusCheckExternalIntegrationsContext must be used within its respective provider'
    )
  }

  return context
}
